import classnames from 'classnames';
import { includes } from 'lodash';
import React, {
  ChangeEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DropdownOption, OrganizedDropdownOption } from '../PrimarySelect';
import './PrimarySelectDropdown.pcss';
import { Spinner, ScrollArea } from '@mm/components';
import { colors } from '@mm/utils';
import { Waypoint } from 'react-waypoint';

type PrimarySelectDropdownProps = {
  onOptionSelect: (option: DropdownOption) => void;
  options?: DropdownOption[];
  organizedOptions?: OrganizedDropdownOption[];
  dropdownTransitionActive: boolean;
  width: 'auto' | number;
  selectedOption: DropdownOption | undefined;
  displayNameKey: string;
  showSearchFilter?: boolean;
  className?: string;
  customOptionRenderer?: (
    option: DropdownOption,
  ) => React.ReactNode | Element[];
  searchRightSide?: React.ReactElement;
  underSearchInsert?: React.ReactElement;
  closeOnSearchRightSideClick?: boolean;
  closeDropdown?: () => void;
  isLoading?: boolean;
  onScrollEnd?: () => void;
};

const getFilteredOptions = (
  options: DropdownOption[],
  searchFilter: string,
  displayNameKey: string,
) => {
  if (searchFilter) {
    return options.filter(option => {
      if (option[displayNameKey]) {
        return includes(
          option[displayNameKey].toLowerCase(),
          searchFilter.toLowerCase(),
        );
      }

      return false;
    });
  }

  return options;
};

const getFilteredOrganizedOptions = (
  organizedOptions: OrganizedDropdownOption[],
  searchFilter: string,
  displayNameKey: string,
) => {
  if (searchFilter) {
    const filteredByOptions = organizedOptions.map(organizedOption => {
      if (
        organizedOption.title
          ?.toLowerCase()
          .includes(searchFilter?.toLowerCase())
      ) {
        return organizedOption;
      }

      return {
        ...organizedOption,
        suggestions: getFilteredOptions(
          organizedOption.suggestions,
          searchFilter,
          displayNameKey,
        ),
      };
    });

    return filteredByOptions.filter(filtered => filtered.suggestions.length);
  }

  return organizedOptions;
};

type DropdownOptionItemsProps = {
  options: DropdownOption[];
  selectedOptionId: number | string;
  onOptionSelect: (option: DropdownOption) => void;
  displayNameKey: string;
  customOptionRenderer?: (
    option: DropdownOption,
  ) => React.ReactNode | Element[] | JSX.Element;
};

const DropdownOptionItems = (props: DropdownOptionItemsProps) => {
  const {
    selectedOptionId,
    options,
    displayNameKey,
    onOptionSelect,
    customOptionRenderer,
  } = props;

  return (
    <>
      {options.map(option => (
        <div
          key={option.id}
          className={classnames('primary-select-dropdown__dropdown-item', {
            'primary-select-dropdown--selected-option':
              selectedOptionId === option.id,
          })}
          onClick={() => onOptionSelect(option)}
        >
          {customOptionRenderer
            ? customOptionRenderer(option)
            : option[displayNameKey]}
        </div>
      ))}
    </>
  );
};

export const PrimarySelectDropdown = (props: PrimarySelectDropdownProps) => {
  const {
    showSearchFilter,
    onOptionSelect,
    options,
    dropdownTransitionActive,
    width,
    selectedOption,
    displayNameKey,
    className,
    organizedOptions,
    customOptionRenderer,
    searchRightSide,
    underSearchInsert,
    closeOnSearchRightSideClick,
    closeDropdown,
    isLoading,
    onScrollEnd,
  } = props;
  const selectedOptionId = selectedOption ? selectedOption.id : '';
  const [searchFilter, setSearchFilter] = useState('');
  const ref = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (ref.current) {
      ref.current.select();
    }
  }, []);

  const classList = classnames('primary-select-dropdown', className, {
    'primary-select-dropdown--dropdown-open': dropdownTransitionActive,
  });

  const style =
    width === 'auto'
      ? {}
      : {
          width: `${width}px`,
        };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSearchFilter(e.target.value);
  };

  const filteredOptions = useMemo(
    () =>
      options
        ? getFilteredOptions(options, searchFilter, displayNameKey)
        : undefined,
    [options, searchFilter, displayNameKey],
  );

  const filteredOrganizedOptions = useMemo(
    () =>
      organizedOptions
        ? getFilteredOrganizedOptions(
            organizedOptions,
            searchFilter,
            displayNameKey,
          )
        : undefined,
    [organizedOptions, searchFilter, displayNameKey],
  );

  return (
    <div className={classList} style={style}>
      {showSearchFilter && (
        <div className="primary-select-dropdown__search-input-container">
          <input
            ref={ref}
            placeholder="Search"
            className="primary-select-dropdown__search-input"
            value={searchFilter}
            onChange={onInputChange}
          />

          {searchRightSide && (
            <div
              className="primary-select-dropdown__search-input--right-side"
              onClick={closeOnSearchRightSideClick ? closeDropdown : () => {}}
            >
              {searchRightSide}
            </div>
          )}
        </div>
      )}

      {underSearchInsert}

      <div className="primary-select-dropdown__search-items">
        <ScrollArea className="primary-select__list">
          {filteredOptions && (
            <DropdownOptionItems
              options={filteredOptions}
              onOptionSelect={onOptionSelect}
              selectedOptionId={selectedOptionId}
              displayNameKey={displayNameKey}
              customOptionRenderer={customOptionRenderer}
            />
          )}

          {filteredOrganizedOptions &&
            filteredOrganizedOptions.map(organizedOption => (
              <div
                key={organizedOption.id}
                className="primary-select-dropdown__dropdown-category"
              >
                <span className="primary-select-dropdown__dropdown-category-text">
                  {organizedOption.title}
                </span>

                <div className="primary-select-dropdown__dropdown-category-items">
                  <DropdownOptionItems
                    options={organizedOption.suggestions}
                    onOptionSelect={onOptionSelect}
                    selectedOptionId={selectedOptionId}
                    displayNameKey={displayNameKey}
                    customOptionRenderer={customOptionRenderer}
                  />
                </div>
              </div>
            ))}
          {!isLoading && onScrollEnd && (
            <Waypoint bottomOffset="-20px" onEnter={onScrollEnd} />
          )}
          {!selectedOptionId && isLoading && (
            <span className="primary-select__loader">
              <Spinner
                show
                size={16}
                position="relative"
                color={colors.accentBlue}
                backgroundColor="transparent"
              />
            </span>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};
